import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import {
  Users, Search, Edit, Trash2, Eye,
  Shield, CheckCircle2, XCircle, UserPlus, Lock, Loader2
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

const statusColors = {
  active: 'bg-green-500/20 text-green-500',
  inactive: 'bg-gray-500/20 text-gray-500',
  suspended: 'bg-red-500/20 text-red-500',
  pending: 'bg-yellow-500/20 text-yellow-500',
};

const roleColors = {
  admin: 'bg-purple-500/20 text-purple-500',
  user: 'bg-blue-500/20 text-blue-500',
  manager: 'bg-orange-500/20 text-orange-500',
};

const emptyForm = { full_name: '', email: '', password: '', phone: '', role: 'user', status: 'active' };

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view'); // view, edit, add, permissions, delete
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Permissions modal state
  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const loadUsers = async () => {
    try {
      const data = await adminApi.getAllUsers('limit=100');
      setUsers(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const data = await adminApi.getAllUsers('limit=100');
        setUsers(data);
      } catch (err) {
        toast.error(err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const openModal = async (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    if (type === 'add') {
      setForm(emptyForm);
    } else if ((type === 'edit' || type === 'view') && user) {
      setForm({
        full_name: user.full_name || '',
        email: user.email || '',
        password: '',
        phone: user.phone || '',
        role: user.role || 'user',
        status: user.status || 'active',
      });
    } else if (type === 'permissions' && user) {
      try {
        const [pagesData, permData] = await Promise.all([
          adminApi.getPages(),
          adminApi.getUserPermissions(user.id),
        ]);
        setPages(pagesData.filter(p => !p.is_admin_only));
        setPermissions(permData.permissions || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load permissions');
        return;
      }
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setForm(emptyForm);
  };

  const handleCreate = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createUser({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        role: form.role,
      });
      toast.success('User created successfully');
      closeModal();
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await adminApi.updateUser(selectedUser.id, {
        full_name: form.full_name,
        phone: form.phone || null,
        role: form.role,
        status: form.status,
      });
      toast.success('User updated successfully');
      closeModal();
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await adminApi.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      closeModal();
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (pageId) => {
    setPermissions(prev =>
      prev.includes(pageId) ? prev.filter(p => p !== pageId) : [...prev, pageId]
    );
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await adminApi.updatePermissions(selectedUser.id, permissions);
      toast.success('Permissions updated successfully');
      closeModal();
      await loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const statCards = [
    { label: 'Total Users', value: users.length, icon: Users, color: 'blue' },
    { label: 'Active', value: users.filter(u => u.status === 'active').length, icon: CheckCircle2, color: 'green' },
    { label: 'Admins', value: users.filter(u => u.role === 'admin').length, icon: Shield, color: 'purple' },
    { label: 'Suspended', value: users.filter(u => u.status === 'suspended').length, icon: XCircle, color: 'red' },
  ];

  return (
    <MainLayout title="User Management" subtitle="Manage all platform users">
      <div data-testid="user-management-page">
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              data-testid={`um-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-4 flex items-center gap-4"
            >
              <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 w-80 rounded-xl input-glass"
                data-testid="user-search-input"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-3 rounded-xl glass-card bg-transparent outline-none"
              data-testid="user-filter-role"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="manager">Manager</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 rounded-xl glass-card bg-transparent outline-none"
              data-testid="user-filter-status"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <motion.button
            onClick={() => openModal('add')}
            className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="add-user-button"
          >
            <UserPlus className="w-5 h-5" />
            Add User
          </motion.button>
        </div>

        {/* Users Table */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="users-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">User</th>
                  <th className="px-6 py-4 text-left">Contact</th>
                  <th className="px-6 py-4 text-left">Role</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Joined</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="users-table-body">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">No users found.</td>
                  </tr>
                ) : filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.id}
                    data-testid={`user-row-${user.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] flex items-center justify-center text-white font-bold">
                          {user.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{user.phone || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${roleColors[user.role]}`}>{user.role}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${statusColors[user.status]}`}>{user.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(user.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <motion.button onClick={() => openModal('view', user)} className="p-2 rounded-lg hover:bg-gray-500/10" whileHover={{ scale: 1.1 }} title="View" data-testid={`view-user-${user.id}`}>
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button onClick={() => openModal('edit', user)} className="p-2 rounded-lg hover:bg-gray-500/10" whileHover={{ scale: 1.1 }} title="Edit" data-testid={`edit-user-${user.id}`}>
                          <Edit className="w-4 h-4" />
                        </motion.button>
                        <motion.button onClick={() => openModal('permissions', user)} className="p-2 rounded-lg hover:bg-gray-500/10" whileHover={{ scale: 1.1 }} title="Permissions" data-testid={`permissions-user-${user.id}`}>
                          <Lock className="w-4 h-4" />
                        </motion.button>
                        <motion.button onClick={() => openModal('delete', user)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500" whileHover={{ scale: 1.1 }} title="Delete" data-testid={`delete-user-${user.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="glass-card p-6 w-full max-w-lg m-4 max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
                data-testid="user-modal"
              >
                <h3 className="text-xl font-bold mb-6">
                  {modalType === 'view' && 'User Details'}
                  {modalType === 'edit' && 'Edit User'}
                  {modalType === 'add' && 'Add New User'}
                  {modalType === 'permissions' && 'User Permissions'}
                  {modalType === 'delete' && 'Delete User'}
                </h3>

                {(modalType === 'view' || modalType === 'edit') && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <input
                        type="text"
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        disabled={modalType === 'view'}
                        className="w-full input-glass"
                        data-testid="form-full-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <input type="email" value={form.email} disabled className="w-full input-glass opacity-70" data-testid="form-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <input
                        type="text"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        disabled={modalType === 'view'}
                        className="w-full input-glass"
                        data-testid="form-phone"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Role</label>
                        <select
                          value={form.role}
                          onChange={(e) => setForm({ ...form, role: e.target.value })}
                          disabled={modalType === 'view'}
                          className="w-full input-glass"
                          data-testid="form-role"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <select
                          value={form.status}
                          onChange={(e) => setForm({ ...form, status: e.target.value })}
                          disabled={modalType === 'view'}
                          className="w-full input-glass"
                          data-testid="form-status"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {modalType === 'add' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full input-glass" placeholder="Enter full name" data-testid="form-full-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full input-glass" placeholder="Enter email" data-testid="form-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Password</label>
                      <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full input-glass" placeholder="Enter password" data-testid="form-password" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full input-glass" placeholder="Optional" data-testid="form-phone" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Role</label>
                      <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full input-glass" data-testid="form-role">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                      </select>
                    </div>
                  </div>
                )}

                {modalType === 'permissions' && selectedUser && (
                  <div className="space-y-3" data-testid="permissions-list">
                    <p className="text-sm text-gray-500 mb-4">Manage page access for {selectedUser.full_name}</p>
                    {selectedUser.role === 'admin' ? (
                      <p className="text-sm text-purple-500 flex items-center gap-2"><Shield className="w-4 h-4" />Admins have full access to all pages.</p>
                    ) : pages.map((page) => {
                      const hasAccess = permissions.includes(page.id);
                      return (
                        <div key={page.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-500/5">
                          <span>{page.name}</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hasAccess}
                              onChange={() => togglePermission(page.id)}
                              className="sr-only peer"
                              data-testid={`perm-toggle-${page.id}`}
                            />
                            <div className="w-11 h-6 bg-gray-500/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}

                {modalType === 'delete' && selectedUser && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Are you sure you want to delete <span className="font-semibold">{selectedUser.full_name}</span>? This will permanently remove the user and all their associated cases, clients and documents.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 mt-6">
                  <motion.button onClick={closeModal} className="px-4 py-2 rounded-xl glass-card" whileHover={{ scale: 1.02 }} data-testid="modal-cancel-button">
                    {modalType === 'view' ? 'Close' : 'Cancel'}
                  </motion.button>
                  {modalType === 'add' && (
                    <motion.button onClick={handleCreate} disabled={saving} className="px-4 py-2 rounded-xl btn-primary flex items-center gap-2 disabled:opacity-60" whileHover={{ scale: 1.02 }} data-testid="modal-save-button">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create User
                    </motion.button>
                  )}
                  {modalType === 'edit' && (
                    <motion.button onClick={handleUpdate} disabled={saving} className="px-4 py-2 rounded-xl btn-primary flex items-center gap-2 disabled:opacity-60" whileHover={{ scale: 1.02 }} data-testid="modal-save-button">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
                    </motion.button>
                  )}
                  {modalType === 'permissions' && selectedUser?.role !== 'admin' && (
                    <motion.button onClick={handleSavePermissions} disabled={saving} className="px-4 py-2 rounded-xl btn-primary flex items-center gap-2 disabled:opacity-60" whileHover={{ scale: 1.02 }} data-testid="modal-save-button">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Permissions
                    </motion.button>
                  )}
                  {modalType === 'delete' && (
                    <motion.button onClick={handleDelete} disabled={saving} className="px-4 py-2 rounded-xl bg-red-500 text-white flex items-center gap-2 disabled:opacity-60" whileHover={{ scale: 1.02 }} data-testid="modal-delete-confirm-button">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Delete
                    </motion.button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default UserManagement;
